/**
 * Customer lookup for the Check-In create page §2.
 *
 * Mobile: tappable button opens a bottom-anchored Drawer with search + list.
 *         Avoids the inline-dropdown-under-keyboard problem on phones.
 * Desktop: inline MUI Autocomplete (matches the rest of the app).
 *
 * Data source: getCustomerIndexCached() — 3-tier cache.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { IconSearch, IconX } from '@tabler/icons-react';
import type { CustomerIndexItemDto } from '@techgitter/contracts';

import { getCustomerIndexCached } from '../../services/customerIndexCacheService';
import { Chip } from '../chip';
import { FIELD_SX, OUTLINED_BTN_SX } from '../../styles/designSystem';

export interface CustomerLookupAutocompleteProps {
  onPick: (customer: CustomerIndexItemDto) => void;
  /**
   * When true, the picker defaults to business customers only and shows
   * a "Show all" toggle. Express check-ins set this so reception isn't
   * sifting through residential customers when they're booking in a
   * business drop-off.
   *
   * The toggle UI is also hidden when this is false — the regular flows
   * (in-store / online) don't need it.
   */
  defaultBusinessOnly?: boolean;
}

export const CustomerLookupAutocomplete: React.FC<CustomerLookupAutocompleteProps> = ({
  onPick,
  defaultBusinessOnly = false,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const [allCustomers, setAllCustomers] = useState<CustomerIndexItemDto[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Initialise from the prop. Re-sync when the prop flips (e.g. user toggles
  // intake type from in-store to express) so the filter reflects the new
  // default for that intake type.
  const [businessOnly, setBusinessOnly] = useState(defaultBusinessOnly);
  useEffect(() => {
    setBusinessOnly(defaultBusinessOnly);
  }, [defaultBusinessOnly]);

  useEffect(() => {
    let cancelled = false;
    getCustomerIndexCached()
      .then(res => {
        if (!cancelled) setAllCustomers(res.items);
      })
      .catch(err => console.warn('[CheckIn] customer index load failed:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // The customer list filtered by the business-only toggle. Subsequent
  // search-string filtering layers on top.
  const scopedCustomers = useMemo(
    () => (businessOnly ? allCustomers.filter(isBusiness) : allCustomers),
    [allCustomers, businessOnly],
  );

  const filteredOnDrawer = useMemo(() => {
    if (!search.trim()) return scopedCustomers.slice(0, 40);
    const needle = search.trim().toLowerCase();
    return scopedCustomers
      .filter(c => matchesNeedle(c, needle))
      .slice(0, 60);
  }, [scopedCustomers, search]);

  // Toggle row — only renders when the parent indicates this is a flow
  // where business-only is the appropriate default (express).
  const toggleRow = defaultBusinessOnly ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={!businessOnly}
            onChange={e => setBusinessOnly(!e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography variant="caption" color="text.secondary">
            Show all customers
          </Typography>
        }
        sx={{ ml: 0 }}
      />
      {businessOnly && (
        <Typography variant="caption" color="text.secondary">
          (showing business only)
        </Typography>
      )}
    </Box>
  ) : null;

  if (isPhone) {
    return (
      <>
        {toggleRow}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<IconSearch size={16} stroke={1.5} />}
          onClick={() => setDrawerOpen(true)}
          sx={{ ...OUTLINED_BTN_SX, justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
        >
          Find existing customer…
        </Button>
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: { height: '90dvh', display: 'flex', flexDirection: 'column' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Search by name, business, phone…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              inputProps={{ 'aria-label': 'Search customers' }}
              sx={FIELD_SX}
            />
            <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close" sx={{ ml: 1 }}>
              <IconX size={18} stroke={1.5} />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <List dense disablePadding>
              <ListItemButton
                onClick={() => {
                  setDrawerOpen(false);
                  setSearch('');
                }}
                sx={{ minHeight: 56 }}
              >
                <ListItemText
                  primary="Skip — new customer"
                  secondary="Type details below instead"
                />
              </ListItemButton>
              {filteredOnDrawer.map(c => (
                <ListItemButton
                  key={c.dataverseCustomerId}
                  onClick={() => {
                    onPick(c);
                    setDrawerOpen(false);
                    setSearch('');
                  }}
                  sx={{ minHeight: 56 }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                        <span>{c.displayName}</span>
                        {renderDesignationChip(c.designation)}
                      </Stack>
                    }
                    secondary={formatSecondary(c)}
                  />
                </ListItemButton>
              ))}
              {filteredOnDrawer.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    {businessOnly
                      ? 'No business customers match. Toggle "Show all customers" above, or tap "Skip — new customer".'
                      : 'No matches. Tap "Skip — new customer" above to enter details manually.'}
                  </Typography>
                </Box>
              )}
            </List>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Box>
      {toggleRow}
      <Autocomplete<CustomerIndexItemDto>
        sx={FIELD_SX}
        options={scopedCustomers}
        getOptionLabel={c => c.displayName}
        filterOptions={(options, state) => {
          if (!state.inputValue.trim()) return options.slice(0, 40);
          const needle = state.inputValue.toLowerCase();
          return options.filter(c => matchesNeedle(c, needle)).slice(0, 60);
        }}
        renderOption={(props, c) => (
          <li {...props} key={c.dataverseCustomerId}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Typography variant="body2">{c.displayName}</Typography>
                {renderDesignationChip(c.designation)}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {formatSecondary(c)}
              </Typography>
            </Box>
          </li>
        )}
        onChange={(_, value) => {
          if (value) onPick(value);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Find existing customer"
            placeholder="Search by name, business, phone…"
          />
        )}
        blurOnSelect
      />
    </Box>
  );
};

function isBusiness(c: CustomerIndexItemDto): boolean {
  return c.customerType === 'Business';
}

function matchesNeedle(c: CustomerIndexItemDto, needle: string): boolean {
  const haystack = [
    c.displayName,
    c.fullName ?? '',
    c.businessName ?? '',
    c.phone ?? '',
    c.mobile ?? '',
    c.email ?? '',
    c.phoneNormalized ?? '',
    c.mobileNormalized ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

function formatSecondary(c: CustomerIndexItemDto): string {
  const bits: string[] = [];
  if (c.businessName && c.businessName !== c.displayName) bits.push(c.businessName);
  if (c.phone || c.mobile) bits.push(c.mobile ?? c.phone ?? '');
  return bits.join(' · ');
}

// Render the raw designation as a small chip alongside the customer name.
// Core / Core+ tiers get a coloured chip so they stand out (those drive
// the "no diagnostic fee at check-in" rule); everything else renders in
// a neutral outline so the receptionist can still see what's set.
function renderDesignationChip(designation: string | null | undefined): React.ReactNode {
  if (!designation) return null;
  const normalized = designation.trim();
  if (!normalized) return null;
  const isCore =
    normalized.toLowerCase() === 'core' ||
    normalized.toLowerCase() === 'core+' ||
    normalized.toLowerCase() === 'coreplus';
  return <Chip tone={isCore ? 'success' : 'neutral'}>{normalized}</Chip>;
}
